import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ArrowIcon, Box, Divider, Flex, Text } from '@modusign/design-system';
import { keyframes } from 'emotion';
import { MouseEventHandler } from 'react';
import { useDispatch } from 'react-redux';

import { formServiceUrl } from '@libs/serviceUrls';

import { recordGTMEvent } from '@app/store/analytics/actions';

import SideBarMenuType from '../SideBarMenuType';

const SIDEBAR_MENUS: {
  type: SideBarMenuType;
  text: string;
}[] = [
  {
    type: 'LOCAL',
    text: '내 컴퓨터',
  },
  {
    type: 'SAMPLE_DOCUMENTS',
    text: '계약서 샘플',
  },
  {
    type: 'CLOUD_DRIVE',
    text: '클라우드 저장소',
  },
];

interface UpLoadModalSideBarProps {
  needDraft: boolean;
  needTemplate: boolean;
  sideBarMenuType: SideBarMenuType;
  onSelect: MouseEventHandler<HTMLButtonElement>;
}

const defaultProps = {
  needDraft: false,
  needTemplate: false,
  sideBarMenuType: 'LOCAL',
};

function UpLoadModalSideBar({
  needDraft,
  needTemplate,
  sideBarMenuType,
  onSelect,
}: UpLoadModalSideBarProps) {
  const dispatch = useDispatch();

  return (
    <Flex
      borderRightColor="lightGray300"
      borderRightStyle="solid"
      borderRightWidth="1px"
      flexDirection="column"
      flexShrink={0}
      justifyContent="space-between"
      minHeight="370px"
      pb={7}
      position="relative"
      pt={6}
      width="180px"
    >
      <Box mb={4} textAlign="right">
        <ul>
          {SIDEBAR_MENUS.map((menu) => (
            <SideBarMenuItem
              key={menu.type}
              name={menu.type}
              selected={sideBarMenuType === menu.type}
              onClick={onSelect}
            >
              {menu.text}
            </SideBarMenuItem>
          ))}
        </ul>
        <Box mb={4} mt={6} mx="auto" width="calc(100% - 40px)">
          <Divider />
        </Box>
        <ul>
          {needTemplate && (
            <SideBarMenuLinkItem name="TEMPLATE" onClick={onSelect}>
              템플릿
            </SideBarMenuLinkItem>
          )}
          {needDraft && (
            <SideBarMenuLinkItem name="DRAFT" onClick={onSelect}>
              작성서식
            </SideBarMenuLinkItem>
          )}
        </ul>
      </Box>
      <Text
        as="a"
        bg="pureBlue25"
        color="textEmphasisBase"
        fontSize="sm"
        fontWeight="bold"
        href={formServiceUrl}
        mx={6}
        py={4}
        sx={{
          borderRadius: '4px',
        }}
        target="_blank"
        textAlign="center"
        onClick={() =>
          dispatch(
            recordGTMEvent('formSearchMenuClick', {
              clickOn: '문서업로드모달',
            })
          )
        }
      >
        더 많은 서식 검색
        <FontAwesomeIcon icon={faMagnifyingGlass} style={{ marginLeft: '2px' }} />
      </Text>
    </Flex>
  );
}

UpLoadModalSideBar.defaultProps = defaultProps;

export default UpLoadModalSideBar;

function SideBarMenuItem({
  children,
  name,
  selected,
  onClick,
}: {
  children: string | null;
  name: string;
  selected?: boolean;
  onClick: MouseEventHandler<HTMLButtonElement>;
}) {
  const sideBarArrow = keyframes`
    0% {
      left: -6px;
      opacity: 0;
      position: relative;
    }

    100% {
      left: 0;
      opacity: 1;
      position: relative;
    }`;

  return (
    <li>
      <Box
        as="button"
        color={selected ? 'textNeutralDark' : 'textNeutralLight'}
        name={name}
        px={7}
        py={4}
        sx={{
          transition: '0.3s font-weight',
          '&:hover': {
            color: 'textNeutralDark',
          },
          '&::before': selected
            ? {
                animation: `${sideBarArrow} 0.4s`,
                borderRight: '3px solid',
                borderTop: '3px solid',
                borderColor: 'orangeYellow500',
                content: '""',
                display: 'inline-block',
                height: '8px',
                mr: '10px',
                transform: 'scaleX(0.9) rotate(45deg)',
                width: '8px',
              }
            : undefined,
        }}
        textAlign="right"
        type="button"
        width="100%"
        onClick={onClick}
      >
        <Text fontSize="lg" fontWeight={selected ? 'bold' : 'regular'} m={0} p={0}>
          {children}
        </Text>
      </Box>
    </li>
  );
}

function SideBarMenuLinkItem({
  children,
  name,
  onClick,
}: {
  children: string | null;
  name: string;
  onClick: MouseEventHandler<HTMLButtonElement>;
}) {
  return (
    <li>
      <Text
        as="button"
        color="textNeutralLight"
        name={name}
        pt={4}
        px={7}
        sx={{
          '&:hover > *': {
            display: 'inline-block',
          },
        }}
        onClick={onClick}
      >
        <ArrowIcon direction="right" display="none" mr={1} />
        {children}
      </Text>
    </li>
  );
}
